import React, { useState, useEffect } from "react";

const DynamicColumnDropdown = ({ 
  columns, 
  setColumns, 
  onSave, 
  onClose, 
  primaryColumn = "name",
  columnLabels,
  defaultColumns,
  storageKey
}) => {
  // Maintain local state for smooth UI dragging
  const [localColumns, setLocalColumns] = useState(() => {
    return columns && columns.length > 0 ? columns : defaultColumns;
  });

  // Sync local state if parent forces an update, but only if not currently dragging
  const [isDragging, setIsDragging] = useState(false);
  useEffect(() => {
    if (!isDragging && columns && columns.length > 0) {
      setLocalColumns(columns);
    }
  }, [columns, isDragging]);

  const sortedColumns = [...localColumns].sort((a, b) => a.order - b.order);
  const visibleCols = sortedColumns.filter((c) => c.visible === true || c.visible === "true");
  const hiddenCols = sortedColumns.filter((c) => c.visible === false || c.visible === "false");

  const [draggedItem, setDraggedItem] = useState(null);

  // --- HTML5 Drag & Drop Logic for Reordering ---
  const handleDragStart = (e, index) => {
    setIsDragging(true);
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // Necessary to allow dropping
    if (draggedItem === null || draggedItem === index) return;

    // Evaluate the target column
    const prevSorted = [...localColumns].sort((a, b) => a.order - b.order);
    const prevVisible = prevSorted.filter((c) => c.visible === true || c.visible === "true");
    
    const draggedCol = prevVisible[draggedItem];
    const targetCol = prevVisible[index];

    // STRICT LOCK: Prevent dragging the primary column, OR dropping anything onto the primary column's spot
    if (!draggedCol || !targetCol || draggedCol.name === primaryColumn || targetCol.name === primaryColumn) {
      return;
    }

    setLocalColumns((prevCols) => {
      const currentSorted = [...prevCols].sort((a, b) => a.order - b.order);
      const currentVisible = currentSorted.filter((c) => c.visible === true || c.visible === "true");
      const currentHidden = currentSorted.filter((c) => c.visible === false || c.visible === "false");

      const newVisible = [...currentVisible];
      
      // Reorder visible items
      newVisible.splice(draggedItem, 1);
      newVisible.splice(index, 0, draggedCol);

      // Rebuild and strictly update order indexes
      return [...newVisible, ...currentHidden].map((c, i) => ({
        ...c,
        order: i,
      }));
    });

    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedItem(null);
    
    // Trigger the API and LocalStorage with the strictly sorted current state
    const sortedFinal = [...localColumns].sort((a, b) => a.order - b.order);
    
    setColumns(sortedFinal);
    onSave(sortedFinal);
  };

  // --- Visibility Toggle Logic ---
  const handleToggle = (colName) => {
    // PREVENT PRIMARY COLUMN FROM BEING HIDDEN
    if (colName === primaryColumn) return;

    setLocalColumns((prev) => {
      const updated = prev.map((c) =>
        c.name === colName
          ? { ...c, visible: c.visible === true || c.visible === "true" ? false : true }
          : c
      );
      
      // Update parent and trigger API + LocalStorage
      setColumns(updated);
      onSave(updated);
      return updated;
    });
  };

  // --- Reset Logic ---
  const handleReset = () => {
    setLocalColumns(defaultColumns);
    setColumns(defaultColumns);
    localStorage.setItem(storageKey, JSON.stringify(defaultColumns));
    onSave(defaultColumns);
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-[280px] bg-white rounded-[12px] shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-gray-100 z-[9999] font-outfit text-[#171717] overflow-hidden">
      
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-[15px] text-[#171717]">Columns</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 1L1 11M1 1L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
        {/* Visible Section */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex justify-between items-center mb-3">
            <span className="text-[13px] text-[#71717A] font-medium">Visible ({visibleCols.length})</span>
            <button 
              onClick={handleReset} 
              className="text-[#C95EBE] text-[13px] font-medium hover:underline"
            >
              Reset
            </button>
          </div>
          
          <div className="flex flex-col gap-3">
            {visibleCols.map((col, idx) => {
              const isLocked = col.name === primaryColumn;
              
              return (
                <div
                  key={col.name}
                  draggable={!isLocked}
                  onDragStart={!isLocked ? (e) => handleDragStart(e, idx) : undefined}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={!isLocked ? handleDragEnd : undefined}
                  className={`flex items-center justify-between group ${
                    draggedItem === idx ? "opacity-50 bg-gray-50" : "opacity-100"
                  }`}
                >
                  <label className={`flex items-center gap-3 ${isLocked ? "cursor-default" : "cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={isLocked} 
                      onChange={() => handleToggle(col.name)}
                      className={`w-[18px] h-[18px] rounded border-[#C95EBE] text-[#C95EBE] focus:ring-[#C95EBE] focus:ring-offset-0 accent-[#C95EBE] ${
                        isLocked ? "cursor-default opacity-50" : "cursor-pointer"
                      }`}
                      style={{ accentColor: "#8B55DD" }}
                    />
                    <span className={`text-[14px] font-medium ${isLocked ? "text-gray-500" : "text-[#171717]"}`}>
                      {columnLabels[col.name] || col.name}
                    </span>
                  </label>

                  {/* Drag Handle Icon - Hides entirely for the locked column */}
                  {!isLocked ? (
                    <div className="cursor-grab text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
                        <circle cx="4" cy="2" r="1.5" />
                        <circle cx="8" cy="2" r="1.5" />
                        <circle cx="4" cy="8" r="1.5" />
                        <circle cx="8" cy="8" r="1.5" />
                        <circle cx="4" cy="14" r="1.5" />
                        <circle cx="8" cy="14" r="1.5" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-[12px]"></div> // Spacer to keep layout aligned
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        {hiddenCols.length > 0 && <div className="h-[1px] w-[calc(100%-2rem)] mx-auto bg-gray-100 my-2" />}

        {/* Hidden Section */}
        {hiddenCols.length > 0 && (
          <div className="px-4 pb-4 pt-1">
            <div className="mb-3">
              <span className="text-[13px] text-[#71717A] font-medium">Hidden ({hiddenCols.length})</span>
            </div>
            
            <div className="flex flex-col gap-3">
              {hiddenCols.map((col) => (
                <div key={col.name} className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => handleToggle(col.name)}
                      className="w-[18px] h-[18px] rounded border-gray-300 text-[#C95EBE] focus:ring-[#C95EBE] focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-[14px] text-[#171717] font-medium">
                      {columnLabels[col.name] || col.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicColumnDropdown;